import { useCallback, useEffect, useRef } from "react";
import { useDND, type QueuedNotification } from "../../contexts/DNDContext";
import {
  useServerNotifications,
  type ServerNotification,
} from "../../contexts/NotificationsContext";
import { useSettings } from "../../contexts/SettingsContext";
import { useToast, type NotificationType } from "../../contexts/ToastContext";
import { isAgentTaskNotification } from "../../lib/agent-task-notification-policy";
import { isElectron } from "../../lib/platform";
import type { NotificationPrefs, Severity } from "../../lib/user-preferences";
import { healingBowlPlayer } from "../../utils/healingBowlSounds";
import {
  playAlertSound,
  playIOSPing,
  type AlertType,
} from "../../utils/soundAlerts";
import {
  isRiskFlowCategory,
  normalizedRiskFlowScore,
  notificationSeverity,
} from "./notification-delivery-policy";

type DeliveryNotification = ServerNotification & {
  eventId?: string;
  itemId?: string;
  ivScore?: number | null;
  score?: number | null;
  source?: string;
};

export function NotificationDeliveryBridge() {
  const { notifications, markRead } = useServerNotifications();
  const { preferences, alertConfig } = useSettings();
  const { tryQueue } = useDND();
  const { publishEvent, isNotificationBlocked } = useToast();
  const mountedAt = useRef(Date.now());
  const seen = useRef(new Set<string>());

  const playSound = useCallback(
    (category: string, severity: unknown) => {
      if (!alertConfig.soundEnabled) return;
      if (category === "tilt") {
        healingBowlPlayer.play(alertConfig.healingBowlSound);
      } else if (
        isRiskFlowCategory(category) ||
        category === "risk_signals" ||
        category === "riskSignal"
      ) {
        playIOSPing(true);
      } else {
        const type: AlertType =
          severity === "error" || severity === "critical"
            ? "error"
            : severity === "high" || severity === "warning"
              ? "warning"
              : severity === "success"
                ? "success"
                : "info";
        playAlertSound(type, true);
      }
    },
    [alertConfig.healingBowlSound, alertConfig.soundEnabled],
  );

  const publish = useCallback(
    (notification: DeliveryNotification) => {
      const category = notification.category || "system";
      const notificationType = notificationTypeForCategory(category);
      if (isNotificationBlocked(notificationType)) return;
      if (isRiskFlowCategory(category)) return;
      if (!shouldShow(notification, preferences.notifications)) return;

      const score = normalizedRiskFlowScore(notification);
      const queueItem = toQueuedNotification(notification);
      if (tryQueue(queueItem, criticalTag(notification))) return;

      const eventKey =
        notification.eventId ?? notification.itemId ?? notification.id;
      const url = safeExternalUrl(notification.url);
      const action = url
        ? {
            label: "Open",
            onClick: () => {
              void markRead(notification.id);
              window.open(url, "_blank", "noopener,noreferrer");
            },
          }
        : undefined;
      const critical = notification.severity === "critical";
      const published = publishEvent({
        id: `server-${notification.id}`,
        source: notification.source ?? "server",
        kind: category,
        severity: notificationSeverity(notification.severity),
        title: notification.title,
        body: notification.body,
        dedupeKey: `server:${eventKey}`,
        createdAt: Date.parse(notification.createdAt) || Date.now(),
        durationMs: 5_000,
        persistence: critical && action ? "acknowledgement" : "transient",
        action,
        nativeDeliveryEligible: false,
        notificationType,
        riskScore: score ?? undefined,
        persistentHistoryId: notification.id,
      });
      if (published) playSound(category, notification.severity);
    },
    [
      isNotificationBlocked,
      markRead,
      playSound,
      preferences.notifications,
      publishEvent,
      tryQueue,
    ],
  );

  useEffect(() => {
    (notifications as DeliveryNotification[]).forEach((notification) => {
      if (seen.current.has(notification.id)) return;
      seen.current.add(notification.id);
      if (notification.read || isAgentTaskNotification(notification)) return;
      const createdAt = Date.parse(notification.createdAt) || 0;
      if (createdAt <= mountedAt.current) return;
      publish(notification);
    });
  }, [notifications, publish]);

  return null;
}

function notificationTypeForCategory(category: string): NotificationType {
  if (category === "risk_signals" || category === "riskSignal")
    return "risk-signal";
  if (isRiskFlowCategory(category)) return "riskflow-watch";
  if (category === "tilt") return "tilt-alert";
  if (category === "trade" || category === "toolApprovals")
    return "trade-alert";
  if (category === "system") return "system-update";
  return "news-alert";
}

function shouldShow(
  notification: DeliveryNotification,
  prefs: NotificationPrefs,
) {
  const channel = isElectron()
    ? prefs.deliveryChannels.desktop
    : prefs.deliveryChannels.web;
  if (!channel) return false;
  if (notification.severity === "critical") return true;
  if (prefs.blockedCategories.includes(notification.category as never))
    return false;
  if (prefs.econOnlyMode && notification.category !== "econ_alerts")
    return false;
  if (prefs.criticalOnly || prefs.manualDnd) return false;
  return (
    severityRank(notification.severity) >= severityRank(prefs.severityThreshold)
  );
}

function severityRank(severity: Severity | string) {
  if (severity === "critical" || severity === "error") return 4;
  if (severity === "high" || severity === "warning") return 3;
  if (severity === "medium") return 2;
  return 1;
}

function toQueuedNotification(
  notification: DeliveryNotification,
): QueuedNotification {
  return {
    id: notification.id,
    type: isRiskFlowCategory(notification.category) ? "news" : "system",
    title: notification.title,
    message: notification.body,
    timestamp: new Date(notification.createdAt),
    severity:
      notification.severity === "critical"
        ? "error"
        : notification.severity === "high"
          ? "warning"
          : "info",
  };
}

function criticalTag(notification: DeliveryNotification) {
  if (notification.severity !== "critical") return undefined;
  return notification.title.toLowerCase().includes("margin")
    ? "margin-call"
    : "system-error";
}

function safeExternalUrl(value: string | undefined) {
  if (!value) return null;
  try {
    const url = new URL(value, window.location.origin);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}
